import type {
    MieleStoredGlobals,
    ILanguageObject,
    IDropdownProps,
    ILanguageObjectEntry,
    ILanguageResources,
} from '@/types/shared-types';
import useConfiguration from '@/composables/useConfiguration';
import MieleApiService, { type Instance as MieleApiServiceInstance } from '@/services/miele-api-service';
import { ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { isAllowedDomain, isMultiLingualDomain } from '@/utils/domainUtils';
import { checkQueryClient } from '@/utils/checkQueryclient';

/**
 * useAlternateLanguage
 * This composable is responsible for providing "languageResources" to get utilized
 * in the "DcfDropdown"-Component (which itself get's used in: "Dhf-Header" and "Dhf-Nav"),
 * by initiating the api requests using the "api.constants" as well as "miele-api-service"
 * if not already provided with a languageObject via property.
 */
export const useAlternateLanguage = () => {
    /**
     * isDev
     *
     * enables you to have a language response from XAPI in your
     * local environment. Since it sends a predefined variable
     * for jsonPostBody to it, instead of using window.location.href.
     *
     * If you additionally need to use a local instance of XAPI as well:
     *
     * 1. "pnpm run dev" the Repository: web/core/api-connector (experienceApi)
     * 2. Change: "~/public/c/remote-globals.json" > general.experienceApi to the
     *    address of you local XAPI, usually: "http://localhost:3001/"
     */

    const isDev = window.location.href.includes('localhost');

    const jsonPostBody = !isDev
        ? JSON.stringify({ sourceURL: window.location.href })
        : JSON.stringify({ sourceURL: `https://qweb.miele.ch/de/c/index.htm?cache=no-thank-you_${Date.now()}` }); // test OTWSM-URL
    // : JSON.stringify({ "sourceURL": "https://qweb.miele.ch/de/e/waschmaschinen-1015696-c/" }) // test Hybris-URL

    if (!isDev) {
        // ECOM-2599 Countermeasures
        if (!isAllowedDomain(window.location.href)) return;
        if (!isMultiLingualDomain(window.location.href)) return;
    }

    const { globalsConfig, loadConfig } = useConfiguration();
    const mieleService = ref<MieleApiServiceInstance>();

    /**
     * getLanguageResources:
     * populates the languageResources in order to be passed as property to dcf-language-select
     * @param data ILanguageObject
     * @returns ILanguageResources
     */
    const getLanguageResources = async (data?: ILanguageObject): Promise<ILanguageResources> => {
        // final object
        const languageResources = {
            languageObject: {
                languages: [],
            } as ILanguageObject,

            dcfDropdownProps: {
                options: [],
            } as IDropdownProps,

            currentLanguage: '',
            isLoading: true,
        };

        // passthrough languageObject
        languageResources.languageObject.languages = data ? data.languages : [];

        // set dcfDropdownProps
        data?.languages.map((language: ILanguageObjectEntry) => {
            languageResources.dcfDropdownProps.options.push({
                label: language.name,
                value: language.url,
                is: 'a',
            });
        });

        // set currentLanguageString
        languageResources.currentLanguage =
            data?.languages.find(
                (entry: ILanguageObjectEntry) => entry.hrefLang.toLowerCase() === data?.currentHrefLang?.toLowerCase()
            )?.name ?? '';

        // set isLoading
        languageResources.isLoading = false;

        return languageResources;
    };

    /**
     * fetcher:
     * fetches the endpoint data using the MieleApiService
     * @returns Promise<FetcherResponse>
     */
    const fetcher = async (): Promise<ILanguageObject> => {
        await loadConfig();

        if (globalsConfig.value) {
            mieleService.value = new MieleApiService(globalsConfig.value as MieleStoredGlobals);

            if (isDev) {
                console.info(`[INFO] useAlternateLanguage: "isDev enabled", sending: ${jsonPostBody} to XAPI.`);
            }

            try {
                const res = await mieleService.value.getAlternateLanguages(jsonPostBody);

                // Some targets respond with a languageObject that might not contain the key: "currentHrefLang". In that case, we generate it from globals.
                const concatFromGlobals = `${globalsConfig.value[
                    'general.language'
                ].toLowerCase()}-${globalsConfig.value['general.country'].toUpperCase()}`;
                const currentHrefLang = { currentHrefLang: concatFromGlobals.toString() };

                return !Object.keys(res).includes(Object.keys(currentHrefLang)[0])
                    ? { ...currentHrefLang, ...res } // added key
                    : res; // use existing key
            } catch (err) {
                console.error('[Composable-Error] useAlternateLanguage:', err);
                throw err;
            }
        }

        return { currentHrefLang: '', languages: [] };
    };

    /**
     * useLanguageQuery:
     * Initiates the fetcher using useQuery from TANSTACK in order to enable response-caching
     * @returns any
     */
    const useLanguageQuery = () => {
        checkQueryClient();

        const { data } = useQuery({
            //@ts-expect-error queryKey
            queryKey: [`languages`],
            queryFn: () => fetcher(),
            enabled: mieleService?.value,
        });

        return { data };
    };

    return {
        useLanguageQuery,
        getLanguageResources,
    };
};
